import { useEffect, useRef, useState } from 'react';
import Sidebar from './Sidebar';
import ThemeToggle from './ThemeToggle';
import SettingsModal from './SettingsModal';
import { useEmoji } from '../context/EmojiContext';
import { fetchCurrentUser } from '../services/authService';
import './AppLayout.css';

function getUserInitials(email) {
  const localPart = (email?.split('@')[0] || 'U').replace(/[^a-zA-Z0-9]/g, '');

  if (localPart.length >= 2) {
    return localPart.slice(0, 2).toUpperCase();
  }

  const letter = (localPart.charAt(0) || 'U').toUpperCase();
  return `${letter}${letter}`;
}

function getUserDisplayName(email, role) {
  if (email) {
    const localPart = email.split('@')[0] || '';
    if (localPart) {
      return localPart.charAt(0).toUpperCase() + localPart.slice(1);
    }
  }

  if (role) {
    return role.charAt(0).toUpperCase() + role.slice(1);
  }

  return 'User';
}

function AppLayout({
  activeItem,
  onNavigate,
  sidebarOpen,
  onOpenSidebar,
  onCloseSidebar,
  onLogout,
  children,
}) {
  const { emojify } = useEmoji();
  const [user, setUser] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const profileRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    async function loadUser() {
      try {
        const currentUser = await fetchCurrentUser();
        if (!cancelled) {
          setUser(currentUser);
        }
      } catch {
        if (!cancelled) {
          setUser(null);
        }
      }
    }

    loadUser();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!menuOpen) {
      return undefined;
    }

    function handleClickOutside(event) {
      if (!profileRef.current?.contains(event.target)) {
        setMenuOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuOpen]);

  const displayName = getUserDisplayName(user?.email, user?.role);
  const initials = getUserInitials(user?.email);
  const email = user?.email || 'Loading...';

  return (
    <div className="dashboard">
      <Sidebar
        activeItem={activeItem}
        onNavigate={onNavigate}
        isOpen={sidebarOpen}
        onClose={onCloseSidebar}
      />

      <div className="dashboard__main">
        <header className="dashboard__topbar">
          <div className="dashboard__topbar-left">
            <button
              type="button"
              className="menu-toggle"
              aria-label="Open navigation menu"
              onClick={onOpenSidebar}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true" className="app-icon">
                <path d="M4 7h16v2H4V7zm0 5h16v2H4v-2zm0 5h16v2H4v-2z" fill="currentColor" />
              </svg>
            </button>
          </div>

          <div className="dashboard__topbar-right">
            <ThemeToggle />

            <div className="dashboard__profile-menu" ref={profileRef}>
              <button
                type="button"
                className="dashboard__profile"
                onClick={() => setMenuOpen((open) => !open)}
                aria-expanded={menuOpen}
                aria-haspopup="menu"
              >
                <div className="dashboard__avatar" aria-hidden="true">
                  {initials}
                </div>
                <div className="dashboard__profile-details">
                  <strong>{displayName}</strong>
                  <span>{email}</span>
                </div>
              </button>

              {menuOpen && (
                <div className="dashboard__profile-dropdown" role="menu">
                  <div className="dashboard__profile-dropdown-user">
                    <strong>{displayName}</strong>
                    <span>{email}</span>
                  </div>
                  <button
                    type="button"
                    className="dashboard__profile-dropdown-btn"
                    role="menuitem"
                    onClick={() => {
                      setMenuOpen(false);
                      onNavigate?.('profile');
                    }}
                  >
                    {emojify('👤', 'Profile')}
                  </button>
                  <button
                    type="button"
                    className="dashboard__profile-dropdown-btn"
                    role="menuitem"
                    onClick={() => {
                      setMenuOpen(false);
                      setSettingsOpen(true);
                    }}
                  >
                    {emojify('⚙️', 'Settings')}
                  </button>
                  <button
                    type="button"
                    className="dashboard__profile-dropdown-btn dashboard__profile-dropdown-btn--logout"
                    role="menuitem"
                    onClick={() => {
                      setMenuOpen(false);
                      onLogout?.();
                    }}
                  >
                    {emojify('🚪', 'Log Out')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        <main className="dashboard__content">{children}</main>
      </div>

      {settingsOpen && <SettingsModal onClose={() => setSettingsOpen(false)} />}
    </div>
  );
}

export default AppLayout;
